import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Field } from 'formik'
import { useCustomer } from 'lib/core/hooks'
import { EMPTY_STRING } from 'lib/core/resources/constants'
import { Form, Button, Spinner } from 'lib/ui/components'
import { subscribe } from 'lib/core/data/customer/subscribe'
import { getSlice } from 'lib/core/data/customer/subscribe/selectors'
import { InterceptBlurEvent } from 'lib/ui/components/Form/types'
import { captureEmail } from 'modules/listrak'
import { useStoreView } from 'hooks/useStoreView'
import { Modal } from 'components/Modal'
import { FormValues, schema, validation } from './schema'
import { Wrapper, ModalGlobalStyle } from './styled'
import sofiaBg from 'resources/images/product-selector-background.png'

interface Prop {
  setCustomerEmail: (param: string) => void
}

export const ProductSelectorModal: React.FC<Prop> = ({ setCustomerEmail }) => {
  const { isLoggedIn } = useCustomer()
  const dispatch = useDispatch()
  const { isSuccess } = useSelector(getSlice)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { isSofiaAndGrace } = useStoreView()

  const handleCloseOnSuccess = () => {
    setShowSuccess(true)
    setInterval(() => {
      setIsModalOpen(false)
    }, 1200)
  }

  useEffect(() => {
    if (!isLoggedIn) {
      setIsModalOpen(true)
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (hasSubmitted) {
      if (subscribeToNewsletter) {
        if (isSuccess) handleCloseOnSuccess()
      } else {
        handleCloseOnSuccess()
      }
    }
  }, [hasSubmitted, isSuccess, subscribeToNewsletter])

  const handleSubmit = (values: FormValues) => {
    const data = { ...values }
    const email = data?.email
    setCustomerEmail(email)

    if (subscribeToNewsletter) {
      dispatch(subscribe(email))
    }

    setHasSubmitted(true)
  }

  return (
    <Modal
      isOpen={isModalOpen}
      handleCloseModal={() => setIsModalOpen(false)}
      type='normal'
      backgroundImage={isSofiaAndGrace ? sofiaBg : undefined}
    >
      <Wrapper
        isSofia={isSofiaAndGrace}
      >
        <h1>Get updates and special offers based on your quiz results</h1>
        <Formik
          initialValues={schema}
          validationSchema={validation}
          onSubmit={handleSubmit}
          validateOnMount
        >
          {(formikProps: any) => {
            const { isValid } = formikProps
            return (
              <Form.Wrapper>
                <Field
                  label='Email'
                  name='email'
                  component={Form.Input}
                  required
                  interceptBlur={(e: InterceptBlurEvent<string>) => {
                    captureEmail(e.value)
                  }}
                />
                <label className='input-wrapper'>
                  <input
                    type='checkbox'
                    onChange={() => setSubscribeToNewsletter(!subscribeToNewsletter)}
                  />
                  <span>Subscribe to stay updated with new products and offers.</span>
                </label>
                <div className='actions'>
                  <Button
                    isDisabled={!isValid}
                    type='submit'
                    color='alpha'
                    height={56}
                    padding={78}
                    className={showSuccess ? 'is-success' : EMPTY_STRING}
                  >
                    {hasSubmitted ? (
                      <>
                        {showSuccess ? (
                          <svg width='24' height='24' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M21 6.285l-11.16 12.733-6.84-6.018 1.319-1.49 5.341 4.686 9.865-11.196 1.475 1.285z' />
                          </svg>
                        ) : (
                          <Spinner className='spinner' size={15} />
                        )}
                      </>
                    ) : (
                      <span>Sign Up</span>
                    )}
                  </Button>
                </div>
              </Form.Wrapper>
            )
          }}
        </Formik>
      </Wrapper>
      {isSofiaAndGrace && (
        <ModalGlobalStyle />
      )}
    </Modal>
  )
}
